import { useEffect, useCallback } from 'react';
import { updateLocations } from '../MapMarkers';
import { updateAutoPath, updateCustomPath, setPathVisibility } from '../PathRenderer';
import { updateDistanceLabels, setDistanceLabelsVisibility } from '../DistanceLabels';
import { CITY_ZOOM_THRESHOLD } from '../constants';

export const useMapData = ({
  map,
  mapReady,
  sourcesReadyRef,
  isMountedRef,
  locations,
  startId,
  autoPath,
  customPath,
  showAutoPath,
  showCustomPath,
  showDistanceLabels,
  currentMode
}) => {
  
  // Batch update function to prevent cascading renders
  const batchMapUpdates = useCallback(async () => {
    if (!map.current || !mapReady || !sourcesReadyRef.current || !isMountedRef.current) {
      return;
    }

    // Queue all updates with priorities
    const updates = [
      // High priority: locations (needed by paths)
      () => updateLocations({
        map: map.current,
        mapReady,
        sourcesReady: sourcesReadyRef.current,
        locations,
        startId
      }),
      
      // Medium priority: path visibility
      () => setPathVisibility(
        map.current, 
        mapReady, 
        showAutoPath, 
        showCustomPath
      ),
      
      // Medium priority: path data
      () => updateAutoPath({
        map: map.current,
        mapReady,
        sourcesReady: sourcesReadyRef.current,
        autoPath,
        locations,
        shouldShowAutoPath: showAutoPath
      }),
      
      () => updateCustomPath({
        map: map.current,
        mapReady,
        sourcesReady: sourcesReadyRef.current,
        customPath,
        locations,
        shouldShowCustomPath: showCustomPath
      }),
      
      // Low priority: distance labels
      () => {
        // Always generate distance labels data when needed
        // Visibility control is handled by useAutoHideDistanceLabels hook
        if (showDistanceLabels) {
          // Solve timing issue: ensure distance labels update after map rendering stabilizes
          setTimeout(() => {
            updateDistanceLabels({
              map: map.current,
              mapReady,
              sourcesReady: sourcesReadyRef.current,
              shouldShowDistanceLabels: showDistanceLabels,
              shouldShowAutoPath: showAutoPath,
              shouldShowCustomPath: showCustomPath,
              autoPath,
              customPath,
              locations
            });
            
            // Trigger auto-hide check after data is generated
            // This ensures the visibility is correctly set based on current zoom level
            if (map.current && mapReady) {
              const event = new Event('distanceLabelsUpdated');
              map.current.fire('distanceLabelsUpdated');
            }
          }, 50); // Give map rendering enough time
        }
        
        // Set basic visibility (auto-hide logic will override if needed)
        setDistanceLabelsVisibility(
          map.current, 
          mapReady, 
          showDistanceLabels
        );
      }
    ];

    // Execute updates with smart refresh manager
    updates.forEach((updateFn, index) => {
      updateFn();
    });

  }, [
    map, mapReady, sourcesReadyRef, isMountedRef,
    locations, startId, autoPath, customPath,
    showAutoPath, showCustomPath, showDistanceLabels, currentMode
  ]);
  // Single effect for all map data updates with intelligent batching
  useEffect(() => {
    // Use a small delay to batch rapid state changes
    const timeoutId = setTimeout(() => {
      batchMapUpdates();
    }, 16); // ~60fps

    return () => clearTimeout(timeoutId);
  }, [batchMapUpdates]);

  // Separate effect for mode changes that need immediate visibility updates
  useEffect(() => {
    if (!map.current || !mapReady || !sourcesReadyRef.current) return;

    // Immediate visibility updates for responsive UI
    setPathVisibility(
      map.current, 
      mapReady, 
      showAutoPath, 
      showCustomPath
    );
    
    setDistanceLabelsVisibility(
      map.current, 
      mapReady, 
      showDistanceLabels
    );
  }, [currentMode, mapReady, showAutoPath, showCustomPath, showDistanceLabels]);

  // Note: Individual useEffect hooks have been replaced with the batched update system above
  // This reduces the number of render cycles and improves performance significantly
}; 